// Central registry of editable default email wording.
//
// Every system email whose wording staff may edit is defined here with its
// built-in default subject/body. Saved overrides live in public.email_templates
// (shared default row = brand_id null, optional per-brand row).
//
// Placeholders use the {{token}} style already used by the student intake emails.

export interface TemplatePlaceholder {
  token: string;
  label: string;
  sample: string;
}

export interface EmailTemplateDef {
  key: string;
  label: string;
  group: string;
  description: string;
  placeholders: TemplatePlaceholder[];
  /** Tokens that must remain in the subject or body (e.g. the share link). */
  required: string[];
  subject: string;
  body: string;
}

const P = {
  clientName: { token: "client_name", label: "Client name", sample: "Sarah Nkosi" },
  brandName: { token: "brand_name", label: "Brand name", sample: "Creative Travel Africa" },
  leadTitle: { token: "trip_title", label: "Trip / programme title", sample: "Cape Town & Winelands" },
  quoteNumber: { token: "quote_number", label: "Quote reference", sample: "Q-1042" },
  quoteTotal: { token: "total", label: "Total (formatted, blank if none)", sample: "$12,450.00" },
  validUntil: { token: "valid_until", label: "Valid until date", sample: "30 Sep 2026" },
  link: { token: "link", label: "Secure link", sample: "https://dashboard.creativeinvestments.africa/quote/abc123" },
  programmeNumber: { token: "programme_number", label: "Programme reference", sample: "P-2210" },
  guideName: { token: "guide_name", label: "Guide credit (blank if no guide)", sample: "led by Thabo Molefe" },
  studentName: { token: "name", label: "Student name", sample: "Ana" },
  guestName: { token: "guest_name", label: "Guest name", sample: "Sarah Nkosi" },
  bookingNumber: { token: "booking_number", label: "Booking number", sample: "CB-10234" },
  experienceName: { token: "experience_name", label: "Experience name", sample: "Jazz Night in Cape Town" },
  experienceSummary: {
    token: "experience_summary",
    label: "Experience summary (name or '3 experiences')",
    sample: "3 experiences",
  },
  amount: { token: "amount", label: "Amount due (formatted)", sample: "ZAR 1,250.00" },
  senderName: { token: "sender_name", label: "Sender / staff name", sample: "Natasha Moses" },
  recipientName: { token: "recipient_name", label: "Recipient name", sample: "Thabo Molefe" },
  docCount: { token: "document_count", label: "Number of documents", sample: "3 documents" },
  inviterName: { token: "inviter_name", label: "Person sending the invitation", sample: "Natasha Moses" },
  roleName: { token: "role", label: "Role being granted", sample: "consultant" },
};

export const EMAIL_TEMPLATE_GROUPS = [
  "Sales & Quotes",
  "Student Intake",
  "Bookings",
  "Sharing & Team",
] as const;

export const EMAIL_TEMPLATES: EmailTemplateDef[] = [
  {
    key: "quote_share",
    label: "Quote email",
    group: "Sales & Quotes",
    description: "Sent to a client when sharing a quote link from a lead.",
    placeholders: [P.clientName, P.brandName, P.leadTitle, P.quoteNumber, P.quoteTotal, P.validUntil, P.link],
    required: ["link"],
    subject: "Your Quote {{quote_number}} from {{brand_name}}",
    body: [
      "Dear {{client_name}},",
      "",
      "We are pleased to present your personalised travel quote for {{trip_title}}.",
      "",
      "Quote Reference: {{quote_number}}",
      "Total: {{total}}",
      "Valid Until: {{valid_until}}",
      "",
      "Please click the link below to view your detailed itinerary and pricing:",
      "{{link}}",
      "",
      "If you have any questions or would like to proceed with this booking, please don't hesitate to contact us.",
      "",
      "Best regards,",
      "{{brand_name}}",
    ].join("\n"),
  },
  {
    key: "programme_share",
    label: "Programme email",
    group: "Sales & Quotes",
    description: "Sent to a client when sharing a travel programme link.",
    placeholders: [P.clientName, P.brandName, P.leadTitle, P.programmeNumber, P.link],
    required: ["link"],
    subject: "Your Travel Programme from {{brand_name}}",
    body: [
      "Dear {{client_name}},",
      "",
      "We are delighted to share your travel programme for {{trip_title}}.",
      "",
      "Programme Reference: {{programme_number}}",
      "",
      "Please click the link below to view your detailed itinerary:",
      "{{link}}",
      "",
      "If you have any questions about your upcoming experience, please don't hesitate to contact us.",
      "",
      "We look forward to hosting you!",
      "",
      "Best regards,",
      "{{brand_name}}",
    ].join("\n"),
  },
  {
    key: "report_share",
    label: "Activity report email",
    group: "Sales & Quotes",
    description: "Sent when sharing an activity / completion report link.",
    placeholders: [P.recipientName, P.leadTitle, P.guideName, P.link],
    required: ["link"],
    subject: "Activity Report: {{trip_title}}",
    body: [
      "Dear {{recipient_name}}",
      "",
      "Please click the link to see the report provided by the guide for your booking.",
      "",
      "{{link}}",
      "",
      "Please do share with us any feedback you receive from your client.",
      "",
      "Thank you",
    ].join("\n"),
  },
  {
    key: "intake_blank",
    label: "Student email — blank",
    group: "Student Intake",
    description: "Starting point for a free-form message to a student.",
    placeholders: [P.studentName, P.link],
    required: [],
    subject: "",
    body: ["Hi {{name}},", "", "{{link}}", "", "Kind regards,", "The team"].join("\n"),
  },
  {
    key: "intake_correct",
    label: "Student email — please correct your submission",
    group: "Student Intake",
    description: "Asks a student to redo only the items you ticked.",
    placeholders: [P.studentName, P.link],
    required: ["link"],
    subject: "Please check your intake details",
    body: [
      "Hi {{name}},",
      "",
      "We noticed something that needs correcting on the details you sent us. Please use your personal secure link below — it only asks for the items we still need, so you do NOT have to fill in the whole form again.",
      "",
      "{{link}}",
      "",
      "The link works once and expires in 7 days.",
      "",
      "Kind regards,",
      "The team",
    ].join("\n"),
  },
  {
    key: "intake_passport",
    label: "Student email — passport scan needed",
    group: "Student Intake",
    description: "Used when the uploaded document is not a passport photo page.",
    placeholders: [P.studentName, P.link],
    required: ["link"],
    subject: "Passport photo page needed",
    body: [
      "Hi {{name}},",
      "",
      "The document you uploaded appears to be an ID card rather than your passport. We need a clear photo or scan of the photo page of your passport (the page with your photograph, name and passport number).",
      "",
      "Please upload it using your personal secure link below — it only asks for that one item, so you do NOT have to fill in the whole form again.",
      "",
      "{{link}}",
      "",
      "The link works once and expires in 7 days.",
      "",
      "Kind regards,",
      "The team",
    ].join("\n"),
  },
  {
    key: "intake_correction",
    label: "Student email — correction link",
    group: "Student Intake",
    description: "Generic short request for a targeted correction.",
    placeholders: [P.studentName, P.link],
    required: ["link"],
    subject: "Quick update needed on your intake details",
    body: [
      "Hi {{name}},",
      "",
      "We need a small update to the details you sent us. Please use your personal secure link below — it only asks for the items we still need, so you do NOT have to fill in the whole form again.",
      "",
      "{{link}}",
      "",
      "The link works once and expires in 7 days.",
      "",
      "Kind regards,",
      "The team",
    ].join("\n"),
  },
  {
    key: "booking_confirmation",
    label: "Booking confirmation",
    group: "Bookings",
    description:
      "Sent automatically when a booking is confirmed. Booking details, pickup notes and the footer stay fixed.",
    placeholders: [P.guestName, P.brandName, P.bookingNumber, P.experienceSummary],
    required: [],
    subject: "Booking Confirmed: {{experience_summary}} - {{booking_number}}",
    body: [
      "Dear {{guest_name}},",
      "",
      "We're excited to confirm your booking for {{experience_summary}}!",
    ].join("\n"),
  },
  {
    key: "booking_payment_link",
    label: "Booking payment link",
    group: "Bookings",
    description: "Sent when a guest is asked to pay for a held booking. The payment button stays fixed.",
    placeholders: [P.guestName, P.brandName, P.bookingNumber, P.experienceName, P.amount],
    required: [],
    subject: "Complete Your Booking - {{booking_number}}",
    body: [
      "Dear {{guest_name}},",
      "",
      "Your booking for {{experience_name}} is held for you. Please complete your payment of {{amount}} using the secure button below.",
    ].join("\n"),
  },
  {
    key: "document_share",
    label: "Documents shared with a third party",
    group: "Sharing & Team",
    description:
      "Sent to guides, hotels and suppliers when sharing travel documents. The link button, access-code and expiry notes stay fixed.",
    placeholders: [P.recipientName, P.senderName, P.docCount],
    required: [],
    subject: "Documents shared with you",
    body: [
      "Hello {{recipient_name}},",
      "",
      "{{sender_name}} has shared {{document_count}} with you.",
    ].join("\n"),
  },
  {
    key: "staff_invitation",
    label: "Staff invitation",
    group: "Sharing & Team",
    description: "Sent when inviting a new team member. The Accept Invitation button stays fixed.",
    placeholders: [P.recipientName, P.inviterName, P.roleName],
    required: [],
    subject: "You've been invited to African Creative Investments",
    body: [
      "Hello {{recipient_name}},",
      "",
      "{{inviter_name}} has invited you to join African Creative Investments as a {{role}}.",
      "",
      "Click the button below to accept your invitation and create your account:",
    ].join("\n"),
  },
  {
    key: "agent_welcome",
    label: "Agent sign-up welcome",
    group: "Bookings",
    description: "Sent automatically when a travel agent signs up through the agent sign-up form.",
    placeholders: [
      { token: "agent_name", label: "Agent first name", sample: "Sarah" },
      P.brandName,
      { token: "agent_code", label: "Agent booking code", sample: "TA_SNKOSI_ZA" },
      { token: "commission", label: "Commission %", sample: "20" },
      { token: "dashboard_link", label: "Agent dashboard link", sample: "https://coffeebeansexperiences.africa/agent-portal" },
      { token: "agent_email", label: "Agent registered email", sample: "sarah@agency.com" },
      { token: "portal_link", label: "Agent portal login page", sample: "https://coffeebeansexperiences.africa/agent-portal" },
    ],
    required: ["agent_code", "dashboard_link"],
    subject: "Welcome to {{brand_name}} — your agent code {{agent_code}}",
    body: [
      "Dear {{agent_name}},",
      "",
      "Thank you for signing up as a {{brand_name}} agent. Your agent account is now active.",
      "",
      "YOUR AGENT CREDENTIALS",
      "Agent code: {{agent_code}}",
      "Commission: {{commission}}% on every booking made with your code",
      "Registered email: {{agent_email}}",
      "",
      "How to use your code:",
      "- When booking online on our website, enter {{agent_code}} in the code box at checkout.",
      "- When booking with our team by email or WhatsApp, quote {{agent_code}} so the booking is linked to you.",
      "",
      "YOUR AGENT DASHBOARD & RESOURCES",
      "Your personal dashboard shows every booking made with your code, its status and your commission. From the dashboard you can also open the Resources tab with our wholesale rates, policies, media banks and contacts.",
      "{{dashboard_link}}",
      "",
      "HOW TO LOG IN IN THE FUTURE",
      "- Bookmark the link above — it opens your dashboard directly. Please keep it private.",
      "- On another device, go to {{portal_link}} and enter your registered email ({{agent_email}}).",
      "- We will email you a 6-digit one-time PIN (valid for 15 minutes) plus a one-click sign-in link. No password needed.",
      "",
      "Warm regards,",
      "The {{brand_name}} team",
    ].join("\n"),
  },
];

export function getTemplateDef(key: string): EmailTemplateDef | undefined {
  return EMAIL_TEMPLATES.find((t) => t.key === key);
}

/** Replace {{tokens}} with values; unknown/blank tokens become empty strings. */
export function fillTemplate(text: string, vars: Record<string, string | undefined | null>): string {
  const filled = text.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_m, token: string) => {
    const v = vars[token];
    return v === undefined || v === null ? "" : String(v);
  });
  // Drop lines that became "Label:" with no value, and collapse 3+ blank lines.
  return filled
    .split("\n")
    .filter((line) => !/^[A-Za-z][A-Za-z ()/&'-]*:\s*$/.test(line.trim()))
    .map((line) => line.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function extractTokens(text: string): string[] {
  return Array.from(text.matchAll(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi)).map((m) => m[1].toLowerCase());
}

export interface TemplateValidation {
  unknown: string[];
  missingRequired: string[];
}

export function validateTemplate(def: EmailTemplateDef, subject: string, body: string): TemplateValidation {
  const allowed = def.placeholders.map((p) => p.token);
  const used = Array.from(new Set([...extractTokens(subject), ...extractTokens(body)]));
  return {
    unknown: used.filter((t) => !allowed.includes(t)),
    missingRequired: def.required.filter((t) => !used.includes(t)),
  };
}

export function sampleVars(def: EmailTemplateDef): Record<string, string> {
  return Object.fromEntries(def.placeholders.map((p) => [p.token, p.sample]));
}
